import React, { useMemo, useState } from "react";

import {

	Accordion,

	AccordionDetails,

	AccordionSummary,

	Alert,

	Box,

	Button,

	Chip,

	Divider,

	Paper,

	Stack,

	TextField,

	Typography,

} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";

import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";

import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import dayjs from "dayjs";

import { GlobalState } from "context/GlobalContext";

import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";

import { cardSx, compactDateSlotProps, compactOptionalFieldSx, compactTextFieldSx, primaryButtonSx, SectionCard } from "../sharedStyles";



const VERDICT_META = {

	active: {

		severity: "success",

		icon: <CheckCircleOutlineIcon />,

		color: "#2e7d32",

	},

	not_active: {

		severity: "warning",

		icon: <HelpOutlineOutlinedIcon />,

		color: "#ed6c02",

	},

	not_found: {

		severity: "info",

		icon: <HighlightOffOutlinedIcon />,

		color: "#0288d1",

	},

};



function OfferResultCard({ offer, active }) {

	return (

		<Paper

			elevation={0}

			sx={{

				...cardSx,

				p: 2,

				borderColor: active ? "rgba(46, 125, 50, 0.35)" : "#e8e8e8",

				bgcolor: active ? "rgba(46, 125, 50, 0.04)" : "#fff",

			}}

		>

			<Stack spacing={1.25}>

				<Stack

					direction="row"

					justifyContent="space-between"

					alignItems="flex-start"

					gap={1}

					flexWrap="wrap"

				>

					<Typography fontWeight={700}>{offer.offer_text || "Offer"}</Typography>

					<Chip

						size="small"

						label={active ? "Active" : "Not active"}

						color={active ? "success" : "default"}

						variant={active ? "filled" : "outlined"}

					/>

				</Stack>



				<Stack direction="row" gap={1} flexWrap="wrap">

					{offer.offer_type && (

						<Chip size="small" label={offer.offer_type} variant="outlined" />

					)}

					{(offer.valid_from || offer.valid_to) && (

						<Chip

							size="small"

							variant="outlined"

							label={`${offer.valid_from || "?"} → ${offer.valid_to || "?"}`}

						/>

					)}

					{offer.closed_at && (

						<Chip

							size="small"

							color="warning"

							variant="outlined"

							label={`Force closed: ${offer.closed_at}`}

						/>

					)}

					{offer.brand && (

						<Chip size="small" variant="outlined" label={`Brand: ${offer.brand}`} />

					)}

				</Stack>



				{!!offer.applicable_days?.length && (

					<Typography variant="body2" color="text.secondary">

						Days: {offer.applicable_days.join(", ")}

					</Typography>

				)}



				{!!offer.store_codes?.length && (

					<Typography variant="body2" color="text.secondary">

						Stores: {offer.store_codes.slice(0, 8).join(", ")}

						{offer.store_codes.length > 8

							? ` +${offer.store_codes.length - 8} more`

							: ""}

					</Typography>

				)}



				{!!offer.blockers?.length && (

					<Alert severity="warning" sx={{ py: 0.5, borderRadius: 2 }}>

						{offer.blockers.join(" · ")}

					</Alert>

				)}



				{!!offer.notes?.length && (

					<Typography variant="caption" color="text.secondary">

						{offer.notes.join(" · ")}

					</Typography>

				)}

			</Stack>

		</Paper>

	);

}



function OfferChecker() {

	const { setSnackBar, setSnackBarOpen } = GlobalState();

	const [barcode, setBarcode] = useState("");

	const [billDate, setBillDate] = useState(dayjs());

	const [storeCode, setStoreCode] = useState("");

	const [storeName, setStoreName] = useState("");

	const [loading, setLoading] = useState(false);

	const [error, setError] = useState("");

	const [result, setResult] = useState(null);



	const canSearch = useMemo(

		() => Boolean(barcode.trim()) && billDate?.isValid() && !loading,

		[barcode, billDate, loading]

	);



	const showSnack = (message, severity = "info") => {

		setSnackBarOpen(true);

		setSnackBar((prev) => ({

			...prev,

			message,

			severity,

			anchorOrigin: { horizontal: "center", vertical: "top" },

		}));

	};



	const handleSearch = async () => {

		if (!barcode.trim()) {

			setError("Enter a barcode to check.");

			return;

		}

		if (!billDate?.isValid()) {

			setError("Select a valid bill date.");

			return;

		}



		try {

			setLoading(true);

			setError("");

			const res = await pythonVmInstance.get("/mbo-payment-working/offers/lookup", {

				params: {

					barcode: barcode.trim(),

					bill_date: billDate.format("YYYY-MM-DD"),

					store_code: storeCode.trim() || undefined,

					store_name: storeName.trim() || undefined,

				},

			});

			setResult(res.data || null);

			showSnack(res.data?.verdict_label || "Lookup complete", "success");

		} catch (lookupError) {

			const message =

				lookupError?.response?.data?.message ||

				lookupError?.message ||

				"Failed to lookup offers";

			setResult(null);

			setError(message);

			showSnack(message, "error");

		} finally {

			setLoading(false);

		}

	};



	const verdictMeta = VERDICT_META[result?.verdict] || VERDICT_META.not_found;



	return (

		<Stack spacing={3}>

			<SectionCard title="Search criteria">
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Stack spacing={1.5}>
						<Stack
							direction="row"
							flexWrap="wrap"
							useFlexGap
							spacing={1.5}
							alignItems="center"
						>
							<TextField
								label="Barcode"
								value={barcode}
								onChange={(e) => setBarcode(e.target.value)}
								autoFocus
								placeholder="Barcode"
								size="small"
								variant="outlined"
								sx={compactTextFieldSx}
								onKeyDown={(e) => {
									if (e.key === "Enter" && canSearch) handleSearch();
								}}
							/>
							<DatePicker
								label="Bill date"
								format="DD/MM/YY"
								value={billDate}
								onChange={(value) => setBillDate(value || null)}
								slotProps={compactDateSlotProps}
							/>
							<Button
								variant="contained"
								size="small"
								startIcon={<SearchOutlinedIcon />}
								onClick={handleSearch}
								disabled={!canSearch}
								sx={{
									...primaryButtonSx,
									height: 36,
									px: 2,
								}}
							>
								{loading ? "Checking..." : "Check"}
							</Button>
						</Stack>

						<Stack
							direction="row"
							flexWrap="wrap"
							useFlexGap
							spacing={1.5}
							alignItems="center"
						>
							<TextField
								label="Store code"
								value={storeCode}
								onChange={(e) => setStoreCode(e.target.value)}
								size="small"
								variant="outlined"
								placeholder="Optional"
								sx={compactOptionalFieldSx}
							/>
							<TextField
								label="Store name"
								value={storeName}
								onChange={(e) => setStoreName(e.target.value)}
								size="small"
								variant="outlined"
								placeholder="Optional"
								sx={compactOptionalFieldSx}
							/>
						</Stack>
					</Stack>
				</LocalizationProvider>

				{error && (
					<Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
						{error}
					</Alert>
				)}
			</SectionCard>



			{!result && !loading && (

				<Box

					sx={{

						py: 6,

						px: 3,

						textAlign: "center",

						borderRadius: 3,

						border: "1px dashed #e0e0e0",

						bgcolor: "rgba(0, 0, 0, 0.02)",

					}}

				>

					<Box

						sx={{

							width: 56,

							height: 56,

							mx: "auto",

							mb: 1.5,

							borderRadius: "50%",

							display: "grid",

							placeItems: "center",

							bgcolor: "rgba(33, 33, 33, 0.06)",

							color: "#212121",

						}}

					>

						<LocalOfferOutlinedIcon />

					</Box>

					<Typography variant="subtitle1" fontWeight={600} gutterBottom>

						No results yet

					</Typography>

					<Typography variant="body2" color="text.secondary" maxWidth={420} mx="auto">

						Enter a barcode and bill date above to see active offers, inactive

						offers, and blockers.

					</Typography>

				</Box>

			)}



			{result && (

				<Stack spacing={2.5}>

					<Paper

						elevation={0}

						sx={{

							...cardSx,

							p: 2.5,

							borderColor: verdictMeta.color,

							bgcolor:

								result.verdict === "active"

									? "rgba(46, 125, 50, 0.06)"

									: result.verdict === "not_active"

										? "rgba(237, 108, 2, 0.06)"

										: "rgba(2, 136, 209, 0.06)",

						}}

					>

						<Stack spacing={1}>

							<Stack direction="row" spacing={1} alignItems="center">

								{verdictMeta.icon}

								<Typography variant="h6" fontWeight={700}>

									{result.verdict_label}

								</Typography>

							</Stack>

							<Typography variant="body2" color="text.secondary">

								Barcode <b>{result.barcode}</b>

								{result.brand ? ` · Brand ${result.brand}` : ""}

								{` · ${result.bill_date}`}

								{result.bill_day ? ` (${result.bill_day})` : ""}

								{result.store_code ? ` · Store ${result.store_code}` : ""}

							</Typography>

							<Stack direction="row" gap={1} flexWrap="wrap">

								<Chip size="small" label={`Total ${result.counts?.total || 0}`} />

								<Chip

									size="small"

									color="success"

									label={`Active ${result.counts?.active || 0}`}

								/>

								<Chip

									size="small"

									label={`Not active ${result.counts?.not_active || 0}`}

								/>

								{result.source ? (

									<Chip

										size="small"

										variant="outlined"

										label={

											result.source === "bq_catalog"

												? "Source: catalog"

												: `Source: ${result.source}`

										}

									/>

								) : null}

							</Stack>

						</Stack>

					</Paper>



					{!!result.active_offers?.length && (

						<Stack spacing={1.5}>

							<Typography variant="subtitle1" fontWeight={700}>

								Active on this date

							</Typography>

							{result.active_offers.map((offer, index) => (

								<OfferResultCard

									key={`active-${index}`}

									offer={offer}

									active

								/>

							))}

						</Stack>

					)}



					{!!result.inactive_offers?.length && (

						<Accordion

							defaultExpanded={!result.active_offers?.length}

							sx={{ ...cardSx, "&:before": { display: "none" } }}

						>

							<AccordionSummary expandIcon={<ExpandMoreIcon />}>

								<Typography fontWeight={600}>

									Other offers for this barcode ({result.inactive_offers.length})

								</Typography>

							</AccordionSummary>

							<AccordionDetails>

								<Stack spacing={1.5}>

									{result.inactive_offers.map((offer, index) => (

										<OfferResultCard

											key={`inactive-${index}`}

											offer={offer}

											active={false}

										/>

									))}

								</Stack>

							</AccordionDetails>

						</Accordion>

					)}



					<Divider />

					<Typography variant="caption" color="text.secondary">

						Discount schemes loaded from {result.discount_start_date}

						{result.cache_hit ? " (cached)" : ""}.

					</Typography>

				</Stack>

			)}

		</Stack>

	);

}



export default OfferChecker;

